import React, { useEffect, useState } from "react";
import "src/components/pages/inspection/cameraFrame.scss";
import {
  Button,
  Column,
  Icon,
  Image,
  Input,
  Row,
  Select,
  SelectOption,
  Sheet,
  Text,
} from "@amzn/meridian";
import {
  analyzeEvidence,
  resetCurrentAnalyzedResult,
  setCapturedImageSrc,
  setDoneInspectingCurrentQuestion,
  setImageCapturingMode,
  setInspectionResult,
  setReadyToSubmitReport,
  updateInspectionResults,
} from "src/store/dataSlice";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { useAppDispatch, useAppSelector } from "src/store/store";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import chevronLeftSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-left-small";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import { CameraFacingMode, Evidence } from "src/data/types";
import { initialResult } from "src/data/mockData";
import ImageUploader from "src/components/UI/imageUploader";
import {
  AI,
  COMPLIANT,
  compliantStatus,
  NON_COMPLIANT,
} from "src/constants/constants";
import { InspectionOverrideData } from "./inspectionPage";
type CameraFrameProps = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  activeQuestionIndex: number;
  setActiveQuestionId: React.Dispatch<React.SetStateAction<string>>;
  setActiveQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  totalQuestions?: number;
  onCapture?: (file: File) => void;
  facingMode?: CameraFacingMode;
};

const CameraFrame = ({
  videoRef,
  activeQuestionIndex,
  totalQuestions,
  setActiveQuestionIndex,
  onCapture,
}: CameraFrameProps) => {
  const {
    imageCapturingMode,
    activeQuestion,
    doneInspectingCurrentQuestion,
    capturedImageSrc,
    readyToSubmitReport,
    currentCreatedInspection,
    currentAnalyzedResult,
    inspectionResult,
    inspectionResults,
  } = useAppSelector((state) => state.data);

  const [overrideComment, setOverrideComment] = useState("");
  const [overrideReasonCode, setOverrideReasonCode] = useState("");
  const [openManualOverrideSheet, setOpenManualOverrideSheet] = useState(false);
  const [openInspectionResultSheet, setOpenInspectionResultSheet] =
    useState(false);
  const [inspectionOverrideData, setInspectionOverrideData] =
    useState<InspectionOverrideData>({
      overrideResult: "",
      reasonCode: "",
      evidence: {
        evidenceId: "",
        fileType: "IMAGE_JPEG",
        file: "",
        fileUrl: "",
      },
    });
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (doneInspectingCurrentQuestion) {
      dispatch(setImageCapturingMode(false));
      dispatch(setReadyToSubmitReport(true));
    }
  }, [doneInspectingCurrentQuestion]);
  useEffect(() => {
    if (inspectionResult.questionId) {
      dispatch(updateInspectionResults(inspectionResult));
    }
  }, [inspectionResult]);

  const handleCapture = async () => {
    dispatch(setInspectionResult(initialResult));
    const video = videoRef.current;
    if (!video || !imageCapturingMode) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    // Calculate square crop center area;
    const size = Math.min(vw / 2, vh / 2);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = 1.25 * size;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, sx, sy, size, 1.25 * size, 0, 0, size, 1.25 * size);
    const blob: Blob = await new Promise((resolve, reject) => {
      return canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Capture failed"))),
        "image/jpeg",
        1
      );
    });
    const file = new File([blob], `frame-${Date.now()}.jpg`, {
      type: blob.type,
    });
    //Call analyze API here
    if (activeQuestion?.questionId && currentCreatedInspection.inspectionId) {
      const anaylyzePayload = new FormData();
      anaylyzePayload.append(
        "inspection_id",
        currentCreatedInspection.inspectionId
      );
      anaylyzePayload.append("question_id", activeQuestion?.questionId);
      anaylyzePayload.append("file", file);
      dispatch(analyzeEvidence(anaylyzePayload));
    }

    const url = URL.createObjectURL(blob);
    dispatch(setCapturedImageSrc(url));
    setOpenInspectionResultSheet(true);
    dispatch(setImageCapturingMode(false));
    if (onCapture) onCapture(file);
  };
  const handleUploadEvidence = (file: File) => {
    setInspectionOverrideData((prev) => {
      return { ...prev, evidence: { ...prev.evidence, file: file } };
    });
  };
  const handleAcceptAIAnswerClick = () => {
    if (activeQuestion?.questionId) {
      dispatch(
        setInspectionResult({
          ...inspectionResult,
          confirmedResult: AI,
          questionId: activeQuestion.questionId,
          aiResult: currentAnalyzedResult.aiResult,
          isCompliant: currentAnalyzedResult.aiResult !== NON_COMPLIANT,
          evidences: [
            ...inspectionResult.evidences,
            {
              fileUrl: capturedImageSrc,
              file: inspectionOverrideData.evidence.file,
              fileType: "IMAGE_JPEG",
            },
          ],
        })
      );
      setOpenInspectionResultSheet(false);
      dispatch(setImageCapturingMode(true));
    }
  };
  const handleOverrideSaveAndContinueClick = () => {
    if (activeQuestion?.questionId) {
      dispatch(
        setInspectionResult({
          ...inspectionResult,
          confirmedResult:
            currentAnalyzedResult.aiResult === NON_COMPLIANT
              ? COMPLIANT
              : NON_COMPLIANT,
          questionId: activeQuestion.questionId,
          aiResult: currentAnalyzedResult.aiResult,
          isCompliant: currentAnalyzedResult.aiResult === NON_COMPLIANT,
          isInspectorOverride: true,
          overrideReason: inspectionOverrideData.reasonCode,
          evidences: [
            ...inspectionResult.evidences,
            {
              fileUrl: capturedImageSrc,
              file: inspectionOverrideData.evidence.file,
              fileType: "IMAGE_JPEG",
            },
          ],
        })
      );
      dispatch(setImageCapturingMode(true));
      setOpenInspectionResultSheet(false);
    }
  };
  return (
    <Column className="camera-frame-viewport">
      {imageCapturingMode && (
        <video
          className="image-source-video"
          ref={videoRef}
          autoPlay
          muted
          playsInline
        />
      )}
      <div className="camera-frame-wrapper">
        {imageCapturingMode && (
          <Column className="camera-frame">
            <Column className="top-line-camera-frame" />
            <Column className="left-line-camera-frame" />
            <Column className="right-line-camera-frame" />
            <Column className="bottom-line-camera-frame" />
          </Column>
        )}
        {!imageCapturingMode && (
          <Column className="captured-image-display-card">
            <Image
              src={capturedImageSrc}
              size="100%"
              viewportAspectRatio={0.9}
            />
          </Column>
        )}
      </div>

      <Sheet
        className="ai-answer-dialogue-sheet"
        type="overlay"
        side="bottom"
        open={openInspectionResultSheet}
        onClose={() => {
          setOpenInspectionResultSheet(true);
          dispatch(resetCurrentAnalyzedResult());
        }}
      >
        <div style={{ borderRadius: "21px" }}>
          {openManualOverrideSheet ? (
            <Column>
              <Row alignmentHorizontal="justify">
                <Text type="h400">Manual Override</Text>
                <Button
                  onClick={() => {
                    setOpenManualOverrideSheet(false);
                  }}
                  type="icon"
                >
                  <Icon tokens={closeSmallTokens} />
                </Button>
              </Row>
              <Column spacing={"200"}>
                <Text>Select Answer</Text>
                <Row widths={"fill"}>
                  <Button
                    onClick={() =>
                      setInspectionOverrideData((prev) => {
                        return { ...prev, overrideResult: compliantStatus.YES };
                      })
                    }
                    type={
                      inspectionOverrideData.overrideResult ===
                      compliantStatus.YES
                        ? "primary"
                        : "secondary"
                    }
                    disabled={currentAnalyzedResult.aiResult !== NON_COMPLIANT}
                  >
                    Yes (Accessible)
                  </Button>
                  <Button
                    onClick={() =>
                      setInspectionOverrideData((prev) => {
                        return { ...prev, overrideResult: "No" };
                      })
                    }
                    type={
                      inspectionOverrideData.overrideResult === "No"
                        ? "primary"
                        : "secondary"
                    }
                    disabled={currentAnalyzedResult.aiResult === NON_COMPLIANT}
                  >
                    Not Accessible
                  </Button>
                </Row>
              </Column>
              <Column spacing={"200"}>
                <Text>Reason Code</Text>
                <Select
                  value={overrideReasonCode}
                  onChange={(reason) => {
                    setOverrideReasonCode(reason);
                    setInspectionOverrideData((prev) => {
                      return { ...prev, reasonCode: reason };
                    });
                  }}
                  placeholder="Choose Reason"
                >
                  {" "}
                  <SelectOption value="puppy" label="Puppy" />
                  <SelectOption value="cat" label="Cat" />
                  <SelectOption value="bunny" label="Bunny" />
                </Select>
              </Column>
              <Column spacing={"200"}>
                <Text>Comment (Optional)</Text>
                <Input
                  value={overrideComment}
                  onChange={setOverrideComment}
                  placeholder="Comment here"
                />
              </Column>
              <Column spacing={"200"}>
                <Text>Attach Image Evidence</Text>
                <Row alignmentHorizontal="justify" widths={"fill"}>
                  <Button type="secondary">
                    <Icon tokens={cameraTokens} />
                    Capture
                  </Button>
                  <ImageUploader
                    handleImageUpload={handleUploadEvidence}
                    draggable={false}
                  />
                </Row>
              </Column>
            </Column>
          ) : (
            <Row alignmentHorizontal="justify">
              <Column spacing="100">
                <Text>AI Identified status</Text>
                <Text
                  color={
                    currentAnalyzedResult.aiResult === NON_COMPLIANT
                      ? "error"
                      : "success"
                  }
                  type="h400"
                >
                  {currentAnalyzedResult.aiResult}
                </Text>
              </Column>
              <Button
                onClick={() => {
                  setOpenInspectionResultSheet(false);
                  dispatch(setImageCapturingMode(true));
                }}
                type="icon"
              >
                <Icon tokens={closeSmallTokens} />
              </Button>
            </Row>
          )}
          <Row
            alignmentHorizontal="justify"
            widths={"fill"}
            spacingInset={"400 none none"}
          >
            {openManualOverrideSheet ? (
              <Button
                onClick={() => setOpenManualOverrideSheet(false)}
                type="tertiary"
              >
                Cancel
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setOpenManualOverrideSheet(true);
                }}
                type="tertiary"
              >
                Override
              </Button>
            )}
            {openManualOverrideSheet ? (
              <Button
                onClick={handleOverrideSaveAndContinueClick}
                type="primary"
                disabled={
                  !inspectionOverrideData.evidence.file ||
                  !inspectionOverrideData.overrideResult ||
                  !inspectionOverrideData.reasonCode
                }
              >
                Save and Continue
              </Button>
            ) : (
              <Button onClick={handleAcceptAIAnswerClick} type="primary">
                Accept AI Answer
              </Button>
            )}
          </Row>
        </div>
      </Sheet>
      {imageCapturingMode && !readyToSubmitReport && (
        <Row
          spacingInset="none 500 500"
          alignmentHorizontal="justify"
          widths={["33.333%", "fit", "33.333%"]}
          className="camera-actions-wrapper"
        >
          <div
            className={activeQuestionIndex === 0 ? "hidden-item" : ""}
            onClick={() => {
              setActiveQuestionIndex(activeQuestionIndex - 1);
            }}
          >
            <Row alignmentVertical="center" spacing="200">
              <Icon tokens={chevronLeftSmallTokens} color="inverted" />
              <Text color="inverted">PREVIOUS</Text>
            </Row>
          </div>
          <div
            className="picture-capturing-icon-wrapper"
            onClick={handleCapture}
          >
            <Icon tokens={cameraTokens} />
          </div>
          <div
            onClick={() => {
              if (activeQuestionIndex + 1 === totalQuestions) {
                dispatch(setImageCapturingMode(false));
                dispatch(setDoneInspectingCurrentQuestion(true));
              } else {
                setActiveQuestionIndex(activeQuestionIndex + 1);
              }
              dispatch(setInspectionResult(initialResult));
            }}
          >
            {activeQuestionIndex + 1 === totalQuestions ? (
              <Row alignmentHorizontal="center" spacing="200">
                <Text color="inverted">FINISH</Text>
              </Row>
            ) : (
              <Row alignmentHorizontal="center" spacing="200">
                <Text color="inverted">NEXT</Text>
                <Icon tokens={chevronRightSmallTokens} color="inverted" />
              </Row>
            )}
          </div>
        </Row>
      )}
    </Column>
  );
};

export default CameraFrame;
